import React, { useMemo } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import { countryName, CountryOption, getCountryOptions } from "../../../utils/countries";

interface CountrySelectProps {
  /** ISO 3166-1 alpha-2 code ("PL"), or "" for no country. */
  value: string | null | undefined;
  onChange: (code: string) => void;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  /** Lets the user clear the country (an address-less machine has none). */
  allowEmpty?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
}

/** The one country picker of the app: every country by name, Poland first,
 * searchable. Stores and emits the ISO code. */
export const CountrySelect: React.FC<CountrySelectProps> = ({
  value,
  onChange,
  label,
  required,
  disabled,
  allowEmpty = false,
  error,
  helperText,
}) => {
  const { t, i18n } = useTranslation();
  const options = useMemo(() => getCountryOptions(i18n.language), [i18n.language]);

  const code = (value || "").toUpperCase();
  const selected: CountryOption | null = code
    ? options.find((option) => option.value === code) ?? { value: code, label: countryName(code) }
    : null;

  return (
    <Autocomplete
      size="small"
      fullWidth
      options={options}
      value={selected}
      onChange={(_, option) => onChange(option?.value ?? "")}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, selectedValue) => option.value === selectedValue.value}
      disableClearable={!allowEmpty}
      disabled={disabled}
      noOptionsText={t("No options")}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label ?? t("Country")}
          required={required}
          error={error}
          helperText={helperText}
        />
      )}
    />
  );
};
